// Brand helpers — pure and client-safe (no server-only imports).
import type { CatalogLite } from "./types";

export interface Brand {
  slug: string;
  label: string;
  count: number;
  image?: string;
}

/** "First Row" -> "first-row". Strips accents; empty input gives "". */
export function brandSlug(name?: string | null): string {
  return (name ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Brands holding at least one product, sorted by label. */
export function listBrands(index: CatalogLite[]): Brand[] {
  const map = new Map<string, Brand>();
  for (const p of index) {
    const label = p.brand?.trim();
    if (!label) continue;
    const slug = brandSlug(label);
    if (!slug) continue;
    const existing = map.get(slug);
    if (existing) {
      existing.count += 1;
      if (!existing.image && p.image) existing.image = p.image;
    } else {
      map.set(slug, { slug, label, count: 1, image: p.image });
    }
  }
  return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
}

// ---------------------------------------------------------------------------
// SEO facts for a brand page, derived from the brand's own visible products so
// every brand page carries unique, accurate copy (types, sizes, price range)
// instead of one boilerplate sentence.
// ---------------------------------------------------------------------------

const TYPE_WORDS: [RegExp, string][] = [
  [/\bpolos?\b/i, "polos"],
  [/\b(tee|tees|t-?shirts?|tshirts?)\b/i, "tees"],
  [/\b(hoodies?|sweatshirts?)\b/i, "hoodies"],
  [/\b(jeans|denim)\b/i, "denim"],
  [/\b(pants|joggers?|trousers)\b/i, "pants"],
  [/\bshorts\b/i, "shorts"],
  [/\b(jackets?|outerwear|coats?)\b/i, "jackets"],
  [/\bsets?\b/i, "sets"],
  [/\bdress(es)?\b/i, "dresses"],
  [/\bbeanies?\b/i, "beanies"],
  [/\b(hats?|caps?|truckers?|snapbacks?)\b/i, "hats"],
];

const SIZE_ORDER = ["XXS", "XS", "S", "M", "L", "XL", "XXL", "2XL", "3XL", "XXXL"];

export interface BrandFacts {
  types: string[]; // e.g. ["polos", "pants"], most common first
  minPrice?: number;
  maxPrice?: number;
  sizes: string[]; // ordered, e.g. ["S","M","L","XL"] or waist sizes
}

export function brandFacts(products: CatalogLite[]): BrandFacts {
  const typeCount = new Map<string, number>();
  const sizes = new Set<string>();
  let minPrice: number | undefined;
  let maxPrice: number | undefined;
  for (const p of products) {
    for (const [re, word] of TYPE_WORDS) {
      if (re.test(p.name)) {
        typeCount.set(word, (typeCount.get(word) ?? 0) + 1);
        break;
      }
    }
    if (p.priceNumber > 0) {
      minPrice = minPrice === undefined ? p.priceNumber : Math.min(minPrice, p.priceNumber);
      maxPrice = maxPrice === undefined ? p.priceNumber : Math.max(maxPrice, p.priceNumber);
    }
    for (const o of p.options ?? []) {
      if (/size/i.test(o.name)) o.choices.forEach((c) => sizes.add(c));
    }
  }
  const letter = [...sizes].filter((s) => SIZE_ORDER.includes(s.toUpperCase()));
  const numeric = [...sizes].filter((s) => /^\d+$/.test(s));
  letter.sort((a, b) => SIZE_ORDER.indexOf(a.toUpperCase()) - SIZE_ORDER.indexOf(b.toUpperCase()));
  numeric.sort((a, b) => Number(a) - Number(b));
  return {
    types: [...typeCount.entries()].sort((a, b) => b[1] - a[1]).map(([w]) => w),
    minPrice,
    maxPrice,
    sizes: [...letter, ...numeric],
  };
}

/** "polos, tees and pants" */
export function joinWords(words: string[]): string {
  if (words.length <= 1) return words[0] ?? "";
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

/** Compact size summary: "S–XL" for letter runs, "30–40" for waists, both joined with "; waist ". */
export function sizeSummary(sizes: string[]): string {
  const letter = sizes.filter((s) => !/^\d+$/.test(s));
  const numeric = sizes.filter((s) => /^\d+$/.test(s));
  const run = (a: string[]) => (a.length > 1 ? `${a[0]}–${a[a.length - 1]}` : a[0] ?? "");
  return [letter.length ? run(letter) : "", numeric.length ? `waist ${run(numeric)}` : ""]
    .filter(Boolean)
    .join(", ");
}
